import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import { useMode } from "../../contexts/ModeContext";
import {
  HomeIcon,
  CubeIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CogIcon,
  GiftIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftRightIcon,
  LifebuoyIcon,
  MegaphoneIcon,
} from "@heroicons/react/24/outline";

const sellerNavItems = [
  { name: "Dashboard", href: "/", icon: HomeIcon },
  { name: "Materials", href: "/materials", icon: CubeIcon },
  { name: "My Bin", href: "/bin", icon: ShoppingCartIcon },
  { name: "Agents", href: "/agents", icon: UserGroupIcon },
  { name: "Bank Details", href: "/banks", icon: CreditCardIcon },
  { name: "Sell", href: "/sell", icon: CurrencyDollarIcon },
  { name: "Sale History", href: "/sales", icon: ClockIcon },
  { name: "Rewards", href: "/rewards", icon: GiftIcon },
  { name: "Referrals", href: "/referrals", icon: UserGroupIcon },
  { name: "Support", href: "/support", icon: LifebuoyIcon },
  { name: "Notifications", href: "/notifications", icon: ClockIcon },
];

const agentNavItems = [
  { name: "Dashboard", href: "/", icon: HomeIcon },
  { name: "Pending Validations", href: "/agent/pending-validations", icon: ClockIcon },
  { name: "Validation History", href: "/agent/validation-history", icon: ClockIcon },
  { name: "Delivery History", href: "/agent/delivery-history", icon: ClockIcon },
  { name: "Bank Details", href: "/banks", icon: CreditCardIcon },
  { name: "Support", href: "/support", icon: LifebuoyIcon },
  { name: "Notifications", href: "/notifications", icon: ClockIcon },
];

const adminNavItems = [
  { name: "Dashboard", href: "/", icon: HomeIcon },
  { name: "Users", href: "/admin/users", icon: UserGroupIcon },
  { name: "Agents", href: "/admin/agents-manage", icon: UserGroupIcon },
  { name: "Manage Materials", href: "/admin/materials", icon: CubeIcon },
  { name: "Pending Validations", href: "/admin/pending-validations", icon: ClockIcon },
  { name: "Validation History", href: "/admin/validation-history", icon: ClockIcon },
  { name: "All Sales", href: "/admin/sales", icon: CurrencyDollarIcon },
  { name: "All Deliveries", href: "/admin/deliveries", icon: ClockIcon },
  { name: "Unpaid payments", href: "/admin/payments", icon: CurrencyDollarIcon },
  { name: "Bounty Management", href: "/admin/bounties", icon: GiftIcon },
  { name: "Rewards Claims", href: "/admin/rewards-management", icon: CogIcon },
  { name: "Contact Management", href: "/admin/contact-management", icon: EnvelopeIcon },
  { name: "Newsletter", href: "/admin/newsletter-management", icon: UserGroupIcon },
  { name: "Mobile Control", href: "/admin/mobile-control", icon: DevicePhoneMobileIcon },
  { name: "Operations", href: "/admin/operations", icon: ChatBubbleLeftRightIcon },
  { name: "Announcements", href: "/admin/announcements", icon: MegaphoneIcon },
  { name: "Settings", href: "/admin/settings", icon: CogIcon },
];

const AppSidebar = () => {
  const { isAdmin, isAgent } = useAuth();
  const { isAgentMode } = useMode();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("jhonks.sidebar") === "collapsed");

  const items = useMemo(() => {
    if (isAdmin) return adminNavItems;
    if (isAgent && isAgentMode) return agentNavItems;
    return sellerNavItems;
  }, [isAdmin, isAgent, isAgentMode]);

  const toggle = () => {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem("jhonks.sidebar", next ? "collapsed" : "open");
      return next;
    });
  };

  const active = (href) =>
    href === "/" ? location.pathname === "/" : location.pathname.startsWith(href);

  return (
    <aside
      className={`hidden lg:flex flex-col sticky top-0 h-screen shrink-0 border-r border-gray-200 bg-white transition-all ${
        collapsed ? "w-20" : "w-64"
      }`}
    >
      <div className="flex items-center justify-end px-3 py-3">
        <button
          type="button"
          onClick={toggle}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
        >
          {collapsed ? <ChevronRightIcon className="h-5 w-5" /> : <ChevronLeftIcon className="h-5 w-5" />}
        </button>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-6">
        {items.map((item) => (
          <Link
            key={item.href + item.name}
            to={item.href}
            title={item.name}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${
              active(item.href)
                ? "bg-green-50 text-green-700"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }`}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {!collapsed && <span>{item.name}</span>}
          </Link>
        ))}
      </nav>
    </aside>
  );
};

export default AppSidebar;
